/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from '@apollo/client';
import { GET_LISTINGS_AND_STOREFRONTS } from './marketPlaceQueries';
import { CheckboxOption } from '../../model/CheckboxOption';
import { LocationBound } from '../../model/Location';
import { flattenObj } from '../../util/flattenObj';
import { MarketPlaceFilters } from '../../model/MarketPlace';
import { generateListingFilters, getCountFilters, getStorefrontFilters } from '../../util/AppUtil';
import { customizeListings, customizeStorefronts } from './transform';

// De-duplicate by id. The listings/storeFronts cache merge policy concatenates
// pages and does not dedupe, so a re-fetch of an already-loaded page (e.g. the
// cache-and-network refresh on revisit) can introduce duplicates; this keeps the
// rendered list clean.
const uniqueById = (items: any[]) => {
  const seen = new Set();
  return (items || []).filter((item) => {
    if (!item || seen.has(item.id)) {
      return false;
    }
    seen.add(item.id);
    return true;
  });
};

const getMapData = (variables: MarketPlaceFilters) => {
  const {
    loading,
    error,
    data,
    previousData,
    fetchMore,
    updateQuery,
    refetch,
    variables: queryVariables
  } = useQuery(GET_LISTINGS_AND_STOREFRONTS, {
    variables: {
      ...variables,
      filters: generateListingFilters(variables),
      personalCountFilters: getCountFilters(variables, 'personal'),
      wantedCountFilters: getCountFilters(variables, 'wanted'),
      storefrontItemsCountFilters: getCountFilters(variables, 'storefrontItem'),
      eventsCountFilters: getCountFilters(variables, 'event'),
      storefrontFilters: getStorefrontFilters(variables)
    },
    fetchPolicy: 'cache-and-network'
  });

  // When the variables change (every map pan/filter), Apollo returns
  // data: undefined until the network answers. Reading straight from `data`
  // made the list drop to a spinner and every count flash to 0 on each pan.
  // Fall back to previousData so the page keeps showing the last results
  // until fresh ones land (loading still reports the fetch for spinners).
  const d = data ?? previousData;

  const listings = flattenObj(d?.listings);
  const storefronts = flattenObj(d?.storeFronts);
  const customizedListings = customizeListings(listings);
  const customizedStorefronts = customizeStorefronts(storefronts);

  // The marketplace list shows the FULL accumulated set (the listings/storeFronts
  // field policies in typePolicies.ts merge each fetchMore page into the cache),
  // so Load more grows the list. We intentionally do NOT slice to one page here
  // (handleMapPageData) — that capped the display at ~9 and blocked Load more.
  return {
    loading,
    error,
    fetchMore,
    refetch,
    updateQuery,
    // The live query variables (filters, sort, user) - Load more hands them
    // to its page query, which does not inherit them on its own.
    variables: queryVariables,
    listings: uniqueById(customizedListings),
    personalListingsCount: d?.personalListingsCount?.meta?.pagination.total,
    wantedListingsCount: d?.wantedListingsCount?.meta?.pagination.total,
    storefrontItemsCount: d?.storefrontItemsCount?.meta?.pagination.total,
    eventsCount: d?.eventsCount?.meta?.pagination.total,
    storefronts: uniqueById(customizedStorefronts),
    storefrontsCount: d?.storeFrontsCount?.meta?.pagination.total
  };
};
export const useGetMarketPlaceData = (
  limit: number,
  type: string,
  moreFilter: any,
  categories: CheckboxOption[] | undefined,
  searchText: string,
  locationBounds: LocationBound,
  listingOffset: number,
  storefrontOffset: number,
  order?: string,
  userId?: string
) => {
  let skip = true;
  const { maxLat, maxLng, minLat, minLng } = locationBounds;
  const categoryIds =
    categories && categories?.length > 0
      ? categories?.map((category: { value: string }) => category.value)
      : moreFilter?.categories?.map((category: { value: string }) => category.value);
  const conditions = moreFilter?.conditions?.map(
    (item_condition: { value: string }) => item_condition.value
  );
  const types = moreFilter?.listings?.map((listing: { value: string }) => listing.value);
  const alternatePriceOptions = moreFilter?.alternatePriceOptions?.map(
    (alternatePrice: { value: string }) => alternatePrice.value
  );
  const priceType = moreFilter?.priceType?.map((priceType: { value: string }) => priceType.value);
  const shippable = moreFilter?.shippableOnly ? true : undefined;
  if (userId) {
    skip = false;
  }
  return getMapData({
    type,
    listingOffset,
    storefrontOffset,
    limit,
    order,
    searchText,
    categoryIds,
    types,
    conditions,
    maxLat,
    maxLng,
    minLat,
    minLng,
    alternatePriceOptions,
    priceType,
    shippable,
    userId,
    skip
  });
};
